import * as React from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";

interface TruncatedCellProps {
  children: React.ReactNode;
  /** Maximum width in pixels (default: 150) */
  maxWidth?: number;
  /** Explicit tooltip text - if not provided, will try to extract from children */
  tooltip?: string;
  /** Additional className */
  className?: string;
}

/**
 * A cell component that truncates text and shows full content in a tooltip.
 * Includes its own TooltipProvider so it can be used anywhere.
 *
 * Usage:
 * ```tsx
 * <TruncatedCell maxWidth={140}>
 *   <DisplayValue value={rental.stationName} />
 * </TruncatedCell>
 *
 * // Or with explicit tooltip
 * <TruncatedCell tooltip={rental.stationName}>
 *   {rental.stationName}
 * </TruncatedCell>
 * ```
 */
export function TruncatedCell({
  children,
  maxWidth = 150,
  tooltip,
  className,
}: TruncatedCellProps) {
  // Extract text for tooltip - use explicit tooltip prop, or try to get from children
  const getTooltipText = (): string | null => {
    if (tooltip) return tooltip;
    if (typeof children === "string") return children;
    // Try to extract from DisplayValue or similar components
    if (React.isValidElement(children) && children.props?.value != null) {
      return String(children.props.value);
    }
    return null;
  };

  const tooltipText = getTooltipText();

  // Don't show tooltip if no text or if it's N/A
  if (!tooltipText || tooltipText === strings("common.notApplicable")) {
    return (
      <span
        className={cn("tw-block tw-truncate", className)}
        style={{ maxWidth }}
      >
        {children}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <span
            className={cn("tw-block tw-truncate tw-cursor-default", className)}
            style={{ maxWidth }}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="tw-max-w-[300px]">
          <p className="tw-break-words">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
